import React, { useState, useEffect, useCallback } from "react";
import { Button, Table, Form, InputGroup, Card, Badge, Modal } from "react-bootstrap";
import { genericSelectGetAll } from "../generic/crud";
import { ENTITY_ID_INVENTORY_ITEM } from "../generic/Constants";

// Inventory types matching the backend enum
const INVENTORY_TYPE = {
    INVENTORY: "INVENTORY",
    SERVICE: "SERVICE"
};

// Format currency
const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 2
    }).format(amount);
};

export function SaleItemsSection({ items, onChange, defaultQuantity = 1, taxPlansLookup = {}, showInventory = true, showServices = true, readOnly = false, customerId = null, isWeightBased = true, createInvoice = false, allowPriceEditProduct = false, allowPriceEditService = false }) {
    const [allItems, setAllItems] = useState([]);
    const [inventoryItems, setInventoryItems] = useState([]);
    const [serviceItems, setServiceItems] = useState([]);
    const [loading, setLoading] = useState(true);

    // State for inventory modal
    const [showInventoryModal, setShowInventoryModal] = useState(false);
    const [inventorySearchText, setInventorySearchText] = useState("");

    // State for service modal
    const [showServiceModal, setShowServiceModal] = useState(false);
    const [serviceSearchText, setServiceSearchText] = useState("");

    // Helper function to get tax percentage from lookup or from the tax object
    const getTaxPercentage = (tax) => {
        // First try to get from the tax object itself
        let percentage = tax.percentage || tax.taxPercentage;

        // If not found, try to look up by ID
        if ((percentage === null || percentage === undefined) && tax.id && taxPlansLookup[tax.id]) {
            percentage = taxPlansLookup[tax.id].taxPercentage;
        }

        // If still not found, try to look up by name (fallback)
        if ((percentage === null || percentage === undefined) && tax.name && taxPlansLookup[tax.name]) {
            percentage = taxPlansLookup[tax.name].taxPercentage;
        }

        return percentage || 0;
    };

    // Helper: get custom price for a customer from an item's customCustomerPricing array
    const getCustomPrice = useCallback((item, custId) => {
        if (!custId || !item.customCustomerPricing || item.customCustomerPricing.length === 0) return null;
        const match = item.customCustomerPricing.find(cp => cp.customerId === custId);
        return match ? match.price : null;
    }, []);

    // Helper: get effective price (custom or default)
    const getEffectivePrice = useCallback((item, custId) => {
        const custom = getCustomPrice(item, custId);
        return custom !== null ? custom : item.price;
    }, [getCustomPrice]);

    // Load inventory items on mount
    useEffect(() => {
        setLoading(true);
        genericSelectGetAll(ENTITY_ID_INVENTORY_ITEM)
            .then(({ data }) => {
                // Map response to standardized format
                // Handle both SelectResponse format (value/label) and raw InventoryItem format (id/itemName)
                const mappedData = (data || []).map(item => ({
                    id: item.value || item.id,
                    name: item.label || item.name || item.itemName,
                    inventoryType: item.inventoryType,
                    price: item.price,
                    unitSymbol: item.unitSymbol,
                    // taxPlan from backend is List<TaxPlan>, map to taxPlans for frontend
                    taxPlans: item.taxPlan || [],
                    hsnCode: item.hsnCode,
                    customCustomerPricing: item.customCustomerPricing || [],
                }));

                setAllItems(mappedData);
                // Filter by inventory type
                const inventory = mappedData.filter(item => item.inventoryType === INVENTORY_TYPE.INVENTORY);
                const services = mappedData.filter(item => item.inventoryType === INVENTORY_TYPE.SERVICE);
                setInventoryItems(inventory);
                setServiceItems(services);
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error loading inventory items:", err);
                setAllItems([]);
                setInventoryItems([]);
                setServiceItems([]);
                setLoading(false);
            });
    }, []);

    // Re-apply custom pricing when the customer (or the loaded master data) changes.
    // Saved line items don't persist the customCustomerPricing array, so we look it
    // up from the inventory master (allItems) by item id and re-price every line.
    // Intentionally NOT depending on `items` so manual price edits aren't reverted.
    useEffect(() => {
        if (!allItems || allItems.length === 0 || !items || items.length === 0) return;
        let changed = false;
        const updatedItems = items.map(item => {
            const master = allItems.find(m => m.id === item.id);
            if (!master) return item;
            const pricing = master.customCustomerPricing || [];
            const custom = customerId ? pricing.find(cp => cp.customerId === customerId) : null;
            const basePrice = master.price ?? item.originalPrice ?? item.price;
            const effectivePrice = custom ? custom.price : basePrice;
            const needsPricingBackfill = (item.customCustomerPricing || []).length !== pricing.length;
            if (item.price !== effectivePrice || item.originalPrice !== basePrice || needsPricingBackfill) {
                changed = true;
                return { ...item, price: effectivePrice, originalPrice: basePrice, customCustomerPricing: pricing };
            }
            return item;
        });
        if (changed) onChange(updatedItems);
    }, [customerId, allItems]);

    // Get current inventory item from items list
    const currentInventoryItem = items.find(item => item.itemType === INVENTORY_TYPE.INVENTORY);

    // Get current service items from items list
    const currentServiceItems = items.filter(item => item.itemType === INVENTORY_TYPE.SERVICE);

    // Keep inventory item quantity in sync with defaultQuantity (net weight) - only for weight-based items
    useEffect(() => {
        if (!isWeightBased) return;
        const invItem = items.find(item => item.itemType === INVENTORY_TYPE.INVENTORY);
        if (invItem && defaultQuantity > 0 && invItem.quantity !== defaultQuantity) {
            const updatedItems = items.map(item => {
                if (item.itemType === INVENTORY_TYPE.INVENTORY) {
                    return { ...item, quantity: defaultQuantity };
                }
                return item;
            });
            onChange(updatedItems);
        }
    }, [defaultQuantity, isWeightBased]);

    // Handle inventory item selection (single item - called when clicking on item in modal)
    const handleInventoryItemSelect = useCallback((selectedItem) => {
        // Remove any existing inventory item and add the new one
        const filteredItems = items.filter(item => item.itemType !== INVENTORY_TYPE.INVENTORY);

        if (selectedItem) {
            const effectivePrice = getEffectivePrice(selectedItem, customerId);
            const newInventoryItem = {
                id: selectedItem.id,
                inventoryItemName: selectedItem.name,
                itemType: INVENTORY_TYPE.INVENTORY,
                quantity: isWeightBased ? defaultQuantity : 1,  // Weight-based: use net weight; otherwise manual entry
                discountAmount: 0,
                // Store extra fields for display
                price: effectivePrice,
                originalPrice: selectedItem.price,
                unitSymbol: selectedItem.unitSymbol,
                taxPlans: selectedItem.taxPlans,
                hsnCode: selectedItem.hsnCode,
                customCustomerPricing: selectedItem.customCustomerPricing,
            };
            onChange([...filteredItems, newInventoryItem]);
        } else {
            onChange(filteredItems);
        }

        // Close modal and reset search
        setShowInventoryModal(false);
        setInventorySearchText("");
    }, [items, onChange, defaultQuantity, customerId, getEffectivePrice, isWeightBased]);

    // Update inventory item quantity
    const handleInventoryQuantityChange = useCallback((value) => {
        const updatedItems = items.map(item => {
            if (item.itemType === INVENTORY_TYPE.INVENTORY) {
                return { ...item, quantity: parseFloat(value) || 0 };
            }
            return item;
        });
        onChange(updatedItems);
    }, [items, onChange]);

    // Update inventory item discount
    const handleInventoryDiscountChange = useCallback((value) => {
        const updatedItems = items.map(item => {
            if (item.itemType === INVENTORY_TYPE.INVENTORY) {
                return { ...item, discountAmount: parseFloat(value) || 0 };
            }
            return item;
        });
        onChange(updatedItems);
    }, [items, onChange]);

    // Add new service item (called when clicking on item in modal)
    const handleAddServiceItem = useCallback((selectedItem) => {
        if (!selectedItem) {
            return;
        }

        const itemToAdd = {
            id: selectedItem.id,
            inventoryItemName: selectedItem.name,
            itemType: INVENTORY_TYPE.SERVICE,
            quantity: 1, // Default quantity
            discountAmount: 0, // Default discount
            // Include price, unit, and tax info for calculations
            price: selectedItem.price,
            unitSymbol: selectedItem.unitSymbol,
            taxPlans: selectedItem.taxPlans,
            hsnCode: selectedItem.hsnCode,
        };

        // Check if service item already exists
        const existingIndex = items.findIndex(
            i => i.id === itemToAdd.id && i.itemType === INVENTORY_TYPE.SERVICE
        );

        if (existingIndex >= 0) {
            // Update existing item quantity
            const updatedItems = [...items];
            updatedItems[existingIndex].quantity += 1;
            onChange(updatedItems);
        } else {
            // Add new item
            onChange([...items, itemToAdd]);
        }

        // Close modal and reset search
        setShowServiceModal(false);
        setServiceSearchText("");
    }, [items, onChange]);

    // Remove service item
    const handleRemoveServiceItem = useCallback((index) => {
        const serviceIndex = items.findIndex((item, i) => {
            let count = 0;
            for (let j = 0; j <= i; j++) {
                if (items[j].itemType === INVENTORY_TYPE.SERVICE) count++;
            }
            return count - 1 === index && item.itemType === INVENTORY_TYPE.SERVICE;
        });

        if (serviceIndex >= 0) {
            const updatedItems = items.filter((_, i) => i !== serviceIndex);
            onChange(updatedItems);
        }
    }, [items, onChange]);

    // Update service item quantity
    const handleUpdateServiceQuantity = useCallback((index, value) => {
        let serviceCount = 0;
        const updatedItems = items.map((item, i) => {
            if (item.itemType === INVENTORY_TYPE.SERVICE) {
                if (serviceCount === index) {
                    serviceCount++;
                    return { ...item, quantity: parseFloat(value) || 0 };
                }
                serviceCount++;
            }
            return item;
        });
        onChange(updatedItems);
    }, [items, onChange]);

    // Update inventory item price
    const handleInventoryPriceChange = useCallback((value) => {
        const updatedItems = items.map(item => {
            if (item.itemType === INVENTORY_TYPE.INVENTORY) {
                return { ...item, price: parseFloat(value) || 0 };
            }
            return item;
        });
        onChange(updatedItems);
    }, [items, onChange]);

    // Update service item price
    const handleUpdateServicePrice = useCallback((index, value) => {
        let serviceCount = 0;
        const updatedItems = items.map((item, i) => {
            if (item.itemType === INVENTORY_TYPE.SERVICE) {
                if (serviceCount === index) {
                    serviceCount++;
                    return { ...item, price: parseFloat(value) || 0 };
                }
                serviceCount++;
            }
            return item;
        });
        onChange(updatedItems);
    }, [items, onChange]);

    // Update service item discount
    const handleUpdateServiceDiscount = useCallback((index, value) => {
        let serviceCount = 0;
        const updatedItems = items.map((item, i) => {
            if (item.itemType === INVENTORY_TYPE.SERVICE) {
                if (serviceCount === index) {
                    serviceCount++;
                    return { ...item, discountAmount: parseFloat(value) || 0 };
                }
                serviceCount++;
            }
            return item;
        });
        onChange(updatedItems);
    }, [items, onChange]);

    // Filter inventory items based on search text
    const filteredInventoryItems = inventoryItems.filter(item =>
        item.name.toLowerCase().includes(inventorySearchText.toLowerCase())
    );

    // Filter service items based on search text
    const filteredServiceItems = serviceItems.filter(item =>
        item.name.toLowerCase().includes(serviceSearchText.toLowerCase())
    );

    if (loading) {
        return (
            <div className="text-center py-4">
                <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                Loading items...
            </div>
        );
    }

    return (
        <div>
            {/* Compact Items Header */}
            <div className="d-flex justify-content-between align-items-center px-4 py-2" style={{ backgroundColor: '#e8f4fc', borderRadius: '8px 8px 0 0' }}>
                <div className="d-flex align-items-center">
                    <i className="bi bi-basket me-2" style={{ color: '#4a90a4' }}></i>
                    <span className="fw-semibold small" style={{ color: '#4a90a4' }}>ITEMS</span>
                </div>
                {!readOnly && (
                    <div className="d-flex gap-2">
                        {showInventory && (
                            <Button
                                variant="light"
                                size="sm"
                                className="text-primary"
                                style={{ border: '1px solid #86b7fe', backgroundColor: '#f8f9fa' }}
                                onClick={() => setShowInventoryModal(true)}
                            >
                                <i className="bi bi-box me-1"></i>
                                {currentInventoryItem ? 'Change Product' : '+ Product'}
                            </Button>
                        )}
                        {showServices && (
                            <Button
                                variant="light"
                                size="sm"
                                className="text-primary"
                                style={{ border: '1px solid #86b7fe', backgroundColor: '#f8f9fa' }}
                                onClick={() => setShowServiceModal(true)}
                            >
                                <i className="bi bi-tools me-1"></i>
                                + Service
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {/* Combined Items Table */}
            {(currentInventoryItem || currentServiceItems.length > 0) ? (
                <Table hover className="mb-0" size="sm">
                    <thead className="table-light">
                        <tr>
                            <th className="ps-3 py-2" style={{ width: readOnly ? (createInvoice ? '30%' : '35%') : (createInvoice ? '35%' : '40%') }}>Item</th>
                            <th className="py-2 text-end" style={{ width: '15%' }}>Rate</th>
                            <th className="py-2 text-center" style={{ width: readOnly ? '10%' : '15%' }}>Qty</th>
                            <th className="py-2 text-center" style={{ width: readOnly ? '10%' : '15%' }}>Discount</th>
                            {createInvoice && <th className="py-2 text-center" style={{ width: '12%' }}>Tax</th>}
                            {readOnly && <th className="py-2 text-end pe-3" style={{ width: '15%' }}>Amount</th>}
                            {!readOnly && <th className="py-2 text-center" style={{ width: '8%' }}></th>}
                        </tr>
                    </thead>
                    <tbody>
                        {/* Inventory Item Row */}
                        {showInventory && currentInventoryItem && (
                            <tr className="bg-primary bg-opacity-10">
                                <td className="ps-3 py-2">
                                    <div className="d-flex align-items-center">
                                        <Badge bg="primary" className="me-2" style={{ fontSize: '0.65rem' }}>PRODUCT</Badge>
                                        <span className="fw-semibold">{currentInventoryItem.inventoryItemName}</span>
                                        {currentInventoryItem.unitSymbol && (
                                            <small className="text-muted ms-1">({currentInventoryItem.unitSymbol})</small>
                                        )}
                                    </div>
                                </td>
                                <td className="py-2 text-end">
                                    {allowPriceEditProduct && !readOnly ? (
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={currentInventoryItem.price || ''}
                                            onChange={(e) => handleInventoryPriceChange(e.target.value)}
                                            size="sm"
                                            className="text-end mx-auto"
                                            style={{ width: '100px' }}
                                        />
                                    ) : (
                                        <span className="text-success fw-semibold">{formatCurrency(currentInventoryItem.price)}</span>
                                    )}
                                    {currentInventoryItem.originalPrice && currentInventoryItem.price !== currentInventoryItem.originalPrice && (
                                        <small className="text-muted text-decoration-line-through d-block" style={{ fontSize: '0.7rem' }}>{formatCurrency(currentInventoryItem.originalPrice)}</small>
                                    )}
                                </td>
                                <td className="py-2 text-center">
                                    {isWeightBased || readOnly ? (
                                        <>
                                            <span className="fw-semibold">{currentInventoryItem.quantity ? parseFloat(currentInventoryItem.quantity).toFixed(3) : '0.000'}</span>
                                            <small className="text-muted d-block" style={{ fontSize: '0.65rem' }}>
                                                {isWeightBased ? 'Net Wt' : (currentInventoryItem.unitSymbol || 'Qty')}
                                            </small>
                                        </>
                                    ) : (
                                        <>
                                            <Form.Control
                                                type="number"
                                                step="0.001"
                                                min="0"
                                                placeholder="0.000"
                                                value={currentInventoryItem.quantity || ''}
                                                onChange={(e) => handleInventoryQuantityChange(e.target.value)}
                                                size="sm"
                                                className="text-center mx-auto"
                                                style={{ width: '80px' }}
                                            />
                                            <small className="text-muted d-block" style={{ fontSize: '0.65rem' }}>
                                                {currentInventoryItem.unitSymbol || 'Qty'}
                                            </small>
                                        </>
                                    )}
                                </td>
                                <td className="py-2 text-center">
                                    {readOnly ? (
                                        <span className="fw-semibold">{currentInventoryItem.discountAmount || 0}</span>
                                    ) : (
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            placeholder="0"
                                            value={currentInventoryItem.discountAmount || ''}
                                            onChange={(e) => handleInventoryDiscountChange(e.target.value)}
                                            size="sm"
                                            className="text-center mx-auto"
                                            style={{ width: '80px' }}
                                        />
                                    )}
                                </td>
                                {createInvoice && (
                                    <td className="py-2 text-center">
                                        {currentInventoryItem.taxPlans && currentInventoryItem.taxPlans.length > 0 ? (
                                            <div className="d-flex flex-wrap gap-1 justify-content-center">
                                                {currentInventoryItem.taxPlans.map((tax, idx) => (
                                                    <Badge key={idx} bg="warning" text="dark" style={{ fontSize: '0.65rem' }}>
                                                        {getTaxPercentage(tax)}%
                                                    </Badge>
                                                ))}
                                            </div>
                                        ) : (
                                            <span className="text-muted">-</span>
                                        )}
                                    </td>
                                )}
                                {readOnly && (() => {
                                    const price = parseFloat(currentInventoryItem.price) || 0;
                                    const qty = parseFloat(currentInventoryItem.quantity) || 0;
                                    const disc = parseFloat(currentInventoryItem.discountAmount) || 0;
                                    const afterDisc = (price * qty) - disc;
                                    let taxAmt = 0;
                                    if (createInvoice && currentInventoryItem.taxPlans) {
                                        currentInventoryItem.taxPlans.forEach(tax => { taxAmt += (afterDisc * getTaxPercentage(tax)) / 100; });
                                    }
                                    return (
                                        <td className="py-2 text-end pe-3">
                                            <span className="fw-semibold">{formatCurrency(afterDisc + taxAmt)}</span>
                                        </td>
                                    );
                                })()}
                                {!readOnly && (
                                    <td className="py-2 text-center">
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="text-danger p-0"
                                            onClick={() => {
                                                const filteredItems = items.filter(item => item.itemType !== INVENTORY_TYPE.INVENTORY);
                                                onChange(filteredItems);
                                            }}
                                        >
                                            <i className="bi bi-x-lg"></i>
                                        </Button>
                                    </td>
                                )}
                            </tr>
                        )}
                        {/* Service Items Rows */}
                        {showServices && currentServiceItems.map((item, index) => (
                            <tr key={index}>
                                <td className="ps-3 py-2">
                                    <div className="d-flex align-items-center">
                                        <Badge bg="success" className="me-2" style={{ fontSize: '0.65rem' }}>SERVICE</Badge>
                                        <span>{item.inventoryItemName || item.id}</span>
                                    </div>
                                </td>
                                <td className="py-2 text-end">
                                    {allowPriceEditService && !readOnly ? (
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={item.price || ''}
                                            onChange={(e) => handleUpdateServicePrice(index, e.target.value)}
                                            size="sm"
                                            className="text-end mx-auto"
                                            style={{ width: '100px' }}
                                        />
                                    ) : (
                                        <span className="text-success">{formatCurrency(item.price)}</span>
                                    )}
                                </td>
                                <td className="py-2 text-center">
                                    {readOnly ? (
                                        <span className="fw-semibold">{item.quantity || 0}</span>
                                    ) : (
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            value={item.quantity}
                                            onChange={(e) => handleUpdateServiceQuantity(index, e.target.value)}
                                            size="sm"
                                            className="text-center mx-auto"
                                            style={{ width: '80px' }}
                                        />
                                    )}
                                </td>
                                <td className="py-2 text-center">
                                    {readOnly ? (
                                        <span className="fw-semibold">{item.discountAmount || 0}</span>
                                    ) : (
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            value={item.discountAmount}
                                            onChange={(e) => handleUpdateServiceDiscount(index, e.target.value)}
                                            size="sm"
                                            className="text-center mx-auto"
                                            style={{ width: '80px' }}
                                        />
                                    )}
                                </td>
                                {createInvoice && (
                                    <td className="py-2 text-center">
                                        {item.taxPlans && item.taxPlans.length > 0 ? (
                                            <div className="d-flex flex-wrap gap-1 justify-content-center">
                                                {item.taxPlans.map((tax, idx) => (
                                                    <Badge key={idx} bg="warning" text="dark" style={{ fontSize: '0.65rem' }}>
                                                        {getTaxPercentage(tax)}%
                                                    </Badge>
                                                ))}
                                            </div>
                                        ) : (
                                            <span className="text-muted">-</span>
                                        )}
                                    </td>
                                )}
                                {readOnly && (() => {
                                    const price = parseFloat(item.price) || 0;
                                    const qty = parseFloat(item.quantity) || 0;
                                    const disc = parseFloat(item.discountAmount) || 0;
                                    const afterDisc = (price * qty) - disc;
                                    let taxAmt = 0;
                                    if (createInvoice && item.taxPlans) {
                                        item.taxPlans.forEach(tax => { taxAmt += (afterDisc * getTaxPercentage(tax)) / 100; });
                                    }
                                    return (
                                        <td className="py-2 text-end pe-3">
                                            <span className="fw-semibold">{formatCurrency(afterDisc + taxAmt)}</span>
                                        </td>
                                    );
                                })()}
                                {!readOnly && (
                                    <td className="py-2 text-center">
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="text-danger p-0"
                                            onClick={() => handleRemoveServiceItem(index)}
                                        >
                                            <i className="bi bi-x-lg"></i>
                                        </Button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            ) : (
                <div className="text-center py-4 text-muted">
                    <i className="bi bi-cart3 fs-2 d-block mb-2 opacity-50"></i>
                    <p className="mb-0 small">No items added</p>
                    <small>Click buttons above to add items</small>
                </div>
            )}

            {/* Select Inventory Item Modal */}
            <Modal show={showInventoryModal} onHide={() => { setShowInventoryModal(false); setInventorySearchText(""); }} centered>
                <Modal.Header closeButton className="bg-primary bg-opacity-10">
                    <Modal.Title className="text-primary">
                        <i className="bi bi-box me-2"></i>
                        Select Inventory Item
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    {/* Search Field */}
                    <div className="p-3 border-bottom">
                        <Form.Control
                            type="text"
                            placeholder="Search inventory items..."
                            value={inventorySearchText}
                            onChange={(e) => setInventorySearchText(e.target.value)}
                            autoFocus
                        />
                        <small className="text-muted">Click on an item to select it</small>
                    </div>

                    {/* Inventory Items Table */}
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <Table hover className="mb-0">
                            <thead className="table-light sticky-top">
                                <tr>
                                    <th className="ps-3">Item Name</th>
                                    <th className="text-end pe-3" style={{ width: '150px' }}>Price</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredInventoryItems.length > 0 ? (
                                    filteredInventoryItems.map((item) => {
                                        const customPrice = getCustomPrice(item, customerId);
                                        const hasCustomPrice = customPrice !== null;
                                        return (
                                            <tr
                                                key={item.id}
                                                onClick={() => handleInventoryItemSelect(item)}
                                                style={{ cursor: 'pointer' }}
                                                className="inventory-item-row"
                                            >
                                                <td className="ps-3 py-3">
                                                    <span className="fw-semibold text-primary">{item.name}</span>
                                                    {item.unitSymbol && (
                                                        <small className="text-muted ms-1">({item.unitSymbol})</small>
                                                    )}
                                                </td>
                                                <td className="text-end pe-3 py-3">
                                                    {hasCustomPrice ? (
                                                        <div>
                                                            <span className="text-success fw-semibold">{formatCurrency(customPrice)}</span>
                                                            <small className="text-muted text-decoration-line-through ms-1">{formatCurrency(item.price)}</small>
                                                        </div>
                                                    ) : (
                                                        <span className="text-success fw-semibold">{formatCurrency(item.price)}</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="2" className="text-center py-4 text-muted">
                                            {inventorySearchText ? 'No items match your search' : 'No inventory items available'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" className="text-secondary" style={{ border: '1px solid #ced4da', backgroundColor: '#f8f9fa' }} onClick={() => { setShowInventoryModal(false); setInventorySearchText(""); }}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Add Service Modal */}
            <Modal show={showServiceModal} onHide={() => { setShowServiceModal(false); setServiceSearchText(""); }} centered>
                <Modal.Header closeButton className="bg-success bg-opacity-10">
                    <Modal.Title className="text-success">
                        <i className="bi bi-tools me-2"></i>
                        Add Service Item
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    {/* Search Field */}
                    <div className="p-3 border-bottom">
                        <Form.Control
                            type="text"
                            placeholder="Search services..."
                            value={serviceSearchText}
                            onChange={(e) => setServiceSearchText(e.target.value)}
                            autoFocus
                        />
                        <small className="text-muted">Click on a service to add it</small>
                    </div>

                    {/* Service Items Table */}
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <Table hover className="mb-0">
                            <thead className="table-light sticky-top">
                                <tr>
                                    <th className="ps-3">Item Name</th>
                                    <th className="text-end pe-3" style={{ width: '150px' }}>Price</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredServiceItems.length > 0 ? (
                                    filteredServiceItems.map((item) => (
                                        <tr
                                            key={item.id}
                                            onClick={() => handleAddServiceItem(item)}
                                            style={{ cursor: 'pointer' }}
                                            className="service-item-row"
                                        >
                                            <td className="ps-3 py-3">
                                                <span className="fw-semibold text-primary">{item.name}</span>
                                                {item.unitSymbol && (
                                                    <small className="text-muted ms-1">({item.unitSymbol})</small>
                                                )}
                                            </td>
                                            <td className="text-end pe-3 py-3">
                                                <span className="text-success fw-semibold">{formatCurrency(item.price)}</span>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="2" className="text-center py-4 text-muted">
                                            {serviceSearchText ? 'No services match your search' : 'No service items available'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" className="text-secondary" style={{ border: '1px solid #ced4da', backgroundColor: '#f8f9fa' }} onClick={() => { setShowServiceModal(false); setServiceSearchText(""); }}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
